import { useState } from 'react';
import { Plus } from '@phosphor-icons/react';
import { RatingBadge } from '@/ui/RatingBadge';
import { useRatings, type FideCadence } from '@/api/ratings';
import { FideRatingInputModal } from './FideRatingInputModal';
import { FideHistoryTable } from './FideHistoryTable';
import { FfeTournamentsTable } from './FfeTournamentsTable';
import { TopOpponentsList } from './TopOpponentsList';

/**
 * FideTab — onglet FIDE détaillé (T6 P1 Stats Hub + enrich PR #I Stats Hub V2).
 *
 * 3 cards par cadence (Standard / Rapide / Blitz) + bouton "Saisir un
 * record" (ouvre FideRatingInputModal) + historique paginé. Si Blitz non
 * classé, on affiche un état "Non classé" (ROADMAP §418 R2).
 *
 * Enrichissement Stats Hub V2 PR #I (2026-05-19) : 2 sections en bas — table
 * des tournois FFE (auto-importée) + top adversaires fréquents. Sources :
 * endpoints `/users/me/ffe/tournaments` et `/users/me/ffe/opponents` (PR #160).
 */

const FIDE_ORDER: FideCadence[] = ['standard', 'rapide', 'blitz'];

export function FideTab(): JSX.Element {
  const ratings = useRatings();
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h2 className="text-heading-3 text-paper-50 m-0">
              <span style={{ color: 'var(--paper-50)' }}>FIDE</span>
              <span className="text-ink-300 text-body ml-2 font-mono uppercase tracking-wider">
                officiel
              </span>
            </h2>
            <p className="text-body-sm text-ink-300 italic mt-1">
              Saisie manuelle — tournois FFE et opens officiels.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-acid-500 text-ink-900 text-body-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus size={16} weight="bold" />
            Saisir un record
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {FIDE_ORDER.map((cad) => {
            const r = ratings.data?.fide?.[cad] ?? null;
            return (
              <RatingBadge
                key={cad}
                platform="fide"
                cadence={cad}
                value={r?.value ?? null}
                updatedAt={r?.rated_at ?? null}
                variant="card"
              />
            );
          })}
        </div>
      </section>

      <FideHistoryTable />

      <section className="mt-6">
        <FfeTournamentsTable />
      </section>

      <section className="mt-6">
        <TopOpponentsList />
      </section>

      <FideRatingInputModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
